import React from 'react';
import type { TransactionReport } from '../types';

interface Props {
  report: TransactionReport;
  onClose: () => void;
}

function fmt(n: number): string {
  if (n === 0) return '—';
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function AccountTransactionsModal({ report, onClose }: Props) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-7xl flex-col rounded-xl bg-white shadow-2xl"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Account Transactions Report</p>
            {report.organisationName && (
              <p className="mt-0.5 text-xs text-slate-500">{report.organisationName}</p>
            )}
            <p className="mt-0.5 text-xs text-slate-400">
              {fmtDate(report.startDate)} – {fmtDate(report.endDate)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable table body */}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[920px] border-collapse text-[11px]">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-slate-300 bg-slate-100">
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Source</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Description</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Reference</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Debit</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Credit</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Running Bal</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Gross</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">GST</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Sup %</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-700">Super</th>
              </tr>
            </thead>
            <tbody>
              {report.sections.map((section) => {
                let runningBal = 0;
                const sectionLabel = section.accountName
                  ? `${section.accountCode} — ${section.accountName}`
                  : section.accountCode;
                return (
                  <React.Fragment key={section.accountCode}>
                    {/* Section header */}
                    <tr className="bg-slate-200">
                      <td
                        colSpan={11}
                        className="px-3 py-1.5 font-semibold text-slate-800"
                      >
                        {sectionLabel}
                      </td>
                    </tr>

                    {/* Transaction rows */}
                    {section.transactions.map((tx, i) => {
                      runningBal = Math.round((runningBal + tx.debit) * 100) / 100;
                      const desc =
                        tx.description && tx.description !== tx.contactName
                          ? `${tx.contactName} — ${tx.description}`
                          : tx.contactName;
                      return (
                        <tr
                          key={i}
                          className="border-b border-slate-100 hover:bg-slate-50"
                        >
                          <td className="whitespace-nowrap px-3 py-1.5 text-slate-700">
                            {fmtDate(tx.date)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
                            {tx.source}
                          </td>
                          <td className="max-w-[280px] truncate px-3 py-1.5 text-slate-700">
                            {desc}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 font-mono text-slate-500">
                            {tx.reference || '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-800">
                            {fmt(tx.debit)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-slate-400">—</td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-700">
                            {fmt(runningBal)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-700">
                            {fmt(tx.gross)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-500">
                            {fmt(tx.gst)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-slate-300">—</td>
                          <td className="px-3 py-1.5 text-right text-slate-300">—</td>
                        </tr>
                      );
                    })}

                    {/* Section subtotal row */}
                    <tr className="border-b-2 border-slate-200 bg-slate-50 font-semibold">
                      <td colSpan={4} className="px-3 py-2 text-slate-700">
                        Total {section.accountCode}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">
                        {fmt(section.subtotalDebit)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-400">—</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">
                        {fmt(section.subtotalDebit)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">
                        {fmt(section.subtotalGross)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">
                        {fmt(section.subtotalGst)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-blue-700">
                        {section.superRate}%
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-bold text-blue-800">
                        {fmt(section.superAmount)}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}

              {/* Grand total row */}
              <tr className="bg-slate-800 font-semibold text-white">
                <td colSpan={4} className="px-3 py-2.5 text-slate-100">
                  Grand Total
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmt(report.grandTotalDebit)}
                </td>
                <td className="px-3 py-2.5 text-right text-slate-500">—</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmt(report.grandTotalDebit)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmt(report.grandTotalGross)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmt(report.grandTotalGst)}
                </td>
                <td className="px-3 py-2.5 text-right text-slate-400"></td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-blue-300 font-bold">
                  {fmt(report.grandSuperAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 px-6 py-3">
          <p className="text-[10px] text-slate-400">
            Super calculated at {report.sections[0]?.superRate ?? 12}% of net debit total per account.
            Debit = LineAmount (ex-GST). Gross = Debit + GST. Data sourced live from Xero.
          </p>
        </div>
      </div>
    </div>
  );
}
